import type { AnimalInfo, AnalysisResult, Appointment, ClarificationQuestion, HealthStatus, Hypothesis, Report } from '../types';

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Ошибка ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function getHealth(): Promise<HealthStatus> {
  const res = await fetch('/api/health');
  return handleResponse<HealthStatus>(res);
}

export async function transcribeAudio(file: File): Promise<{ transcript: string }> {
  const form = new FormData();
  form.append('file', file);
  form.append('language', 'ru');
  const res = await fetch('/api/transcribe', { method: 'POST', body: form });
  return handleResponse<{ transcript: string }>(res);
}

export async function extractInfo(transcript: string): Promise<AnimalInfo> {
  const res = await fetch('/api/extract-info', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transcript }),
  });
  return handleResponse<AnimalInfo>(res);
}

export async function analyze(transcript: string, animalInfo: AnimalInfo): Promise<AnalysisResult> {
  const res = await fetch('/api/analyze', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transcript, animal_info: animalInfo }),
  });
  return handleResponse<AnalysisResult>(res);
}

export async function refineAnalysis(
  transcript: string,
  animalInfo: AnimalInfo,
  previousReport: AnalysisResult,
  additionalData: string
): Promise<AnalysisResult> {
  const res = await fetch('/api/analyze-refine', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      transcript,
      animal_info: animalInfo,
      previous_report: previousReport,
      additional_data: additionalData,
    }),
  });
  return handleResponse<AnalysisResult>(res);
}

export async function finalizeConclusion(
  transcript: string,
  animalInfo: AnimalInfo,
  reports: Report[],
  selectedHypothesis: Hypothesis
): Promise<AnalysisResult> {
  const res = await fetch('/api/finalize', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      transcript,
      animal_info: animalInfo,
      reports,
      selected_hypothesis: selectedHypothesis,
    }),
  });
  return handleResponse<AnalysisResult>(res);
}

export async function requestHypothesisTreatment(
  transcript: string,
  animalInfo: AnimalInfo,
  hypothesis: Hypothesis
): Promise<{ treatment_plan: string[]; medications: Hypothesis['medications']; excluded_medications: Hypothesis['excluded_medications']; clarification_questions?: ClarificationQuestion[]; usage?: Partial<AnalysisResult['usage']> }> {
  const res = await fetch('/api/hypothesis-treatment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transcript, animal_info: animalInfo, hypothesis }),
  });
  return handleResponse<{ treatment_plan: string[]; medications: Hypothesis['medications']; excluded_medications: Hypothesis['excluded_medications']; clarification_questions?: ClarificationQuestion[]; usage?: Partial<AnalysisResult['usage']> }>(res);
}

export async function listAppointments(): Promise<Appointment[]> {
  const res = await fetch('/api/history');
  return handleResponse<Appointment[]>(res);
}

export async function getAppointment(id: number): Promise<Appointment> {
  const res = await fetch(`/api/history/${id}`);
  return handleResponse<Appointment>(res);
}

export async function saveAppointment(data: Partial<Appointment>): Promise<{ id: number; ok: boolean }> {
  const res = await fetch('/api/history', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      transcript: data.transcript,
      animal_info: data.animal_info,
      reports: data.reports,
      model: data.model,
      tokens_in: data.tokens_in,
      tokens_out: data.tokens_out,
      cost: data.cost,
      title: data.title,
    }),
  });
  return handleResponse<{ id: number; ok: boolean }>(res);
}

export async function updateAppointment(id: number, data: Partial<Appointment>): Promise<{ id: number; ok: boolean }> {
  const res = await fetch(`/api/history/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      transcript: data.transcript,
      animal_info: data.animal_info,
      reports: data.reports,
      model: data.model,
      tokens_in: data.tokens_in,
      tokens_out: data.tokens_out,
      cost: data.cost,
      title: data.title,
    }),
  });
  return handleResponse<{ id: number; ok: boolean }>(res);
}

export async function deleteAppointment(id: number): Promise<{ id: number; ok: boolean }> {
  const res = await fetch(`/api/history/${id}`, { method: 'DELETE' });
  return handleResponse<{ id: number; ok: boolean }>(res);
}
